import { useMemo, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { cn } from '../../lib/utils';
import { Quote } from '../../types';
import { EditQuoteDialog } from '../EditQuoteDialog';
import { DateRangePicker, DateRangeValue } from '../ui/date-picker';
import {
  QuoteDetailsDialog,
  QuoteMobileCard,
  QuotesTable,
  formatCurrency,
  sortQuotes,
  calculateQuoteTotals,
  SortColumn,
  SortDirection
} from './quotes';

interface QuotesPageProps {
  // Quotes state
  quotesSearch: string;
  setQuotesSearch: (search: string) => void;
  quotesLoading: boolean;
  quotesFilter: 'all' | 'pending' | 'approved' | 'rejected';
  setQuotesFilter: (filter: 'all' | 'pending' | 'approved' | 'rejected') => void;
  quotesDateRange: DateRangeValue | undefined;
  setQuotesDateRange: (range: DateRangeValue | undefined) => void;
  allQuoteDates: Date[];
  quotes: Quote[];
  selectedQuote: Quote | null;
  quoteDetailsOpen: boolean;
  setQuoteDetailsOpen: (open: boolean) => void;
  quoteEditMode: boolean;
  isUpdatingQuote: boolean;

  // Create quote dialog state
  createQuoteDialogOpen: boolean;
  setCreateQuoteDialogOpen: (open: boolean) => void;

  // Loading states
  quotePdfDownloading: boolean;
  quoteStatusUpdating: boolean;

  // Auth
  accessToken?: string;

  // Materials for edit dialog
  materials?: Array<{ id: string; code: string; name: string; availableThicknesses?: number[]; density: number; pricePerKg: number }>;

  // Handlers
  fetchQuotes: () => Promise<{ success: boolean; error?: string; data?: Quote[] } | void>;
  handleOpenQuoteDialog: (quote: Quote) => Promise<void>;
  handleDownloadQuotePDF: (quoteId: string, quoteNumber: string) => Promise<void>;
  handleUpdateQuoteStatus: (quoteId: string, status: 'pending' | 'approved' | 'rejected') => Promise<void>;
  handleEnterEditMode: () => void;
  handleCancelEdit: () => void;
  handleSaveQuoteChanges: (payload: any) => Promise<void>;
}

export function QuotesPage({
  quotesSearch,
  setQuotesSearch,
  quotesLoading,
  quotesFilter,
  setQuotesFilter,
  quotesDateRange,
  setQuotesDateRange,
  allQuoteDates,
  quotes,
  selectedQuote,
  quoteDetailsOpen,
  setQuoteDetailsOpen,
  quoteEditMode,
  isUpdatingQuote,
  createQuoteDialogOpen,
  setCreateQuoteDialogOpen,
  quotePdfDownloading,
  quoteStatusUpdating,
  accessToken,
  materials,
  fetchQuotes,
  handleOpenQuoteDialog,
  handleDownloadQuotePDF,
  handleUpdateQuoteStatus,
  handleEnterEditMode,
  handleCancelEdit,
  handleSaveQuoteChanges,
}: QuotesPageProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedQuotes = useMemo(() => {
    return sortQuotes(quotes, sortColumn, sortDirection);
  }, [quotes, sortColumn, sortDirection]);

  const quoteTotals = useMemo(() => {
    return calculateQuoteTotals(quotes);
  }, [quotes]);

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Orcamento</span>
        <span>/</span>
        <span className="font-medium text-foreground">Orcamentos de Clientes</span>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle>Orcamentos de Clientes</CardTitle>
            <CardDescription>
              Visualize e gerencie os orcamentos enviados pelos clientes.
            </CardDescription>
          </div>

          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
            <Input
              placeholder="Buscar por numero, cliente..."
              value={quotesSearch}
              onChange={(event) => setQuotesSearch(event.target.value)}
              className="w-full sm:w-64"
            />
            <DateRangePicker
              dateRange={quotesDateRange}
              onDateRangeChange={setQuotesDateRange}
              highlightedDates={allQuoteDates}
              placeholder="Filtrar por data"
              className="w-full sm:w-auto"
            />
            <div className="flex gap-2">
              <Button type="button" onClick={fetchQuotes} disabled={quotesLoading} className="flex-1 sm:flex-initial">
                <RefreshCw className={cn('mr-2 h-4 w-4', quotesLoading && 'animate-spin')} />
                <span className="hidden md:inline">{quotesLoading ? 'Atualizando' : 'Recarregar'}</span>
                <span className="md:hidden">Atualizar</span>
              </Button>
              <Button type="button" onClick={() => setCreateQuoteDialogOpen(true)} className="flex-1 sm:flex-initial">
                <Plus className="mr-2 h-4 w-4" />
                <span className="hidden md:inline">Criar</span>
                <span className="md:hidden">Criar</span>
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Totals Summary */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm border-b pb-3">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Total:</span>
              <span className="font-semibold">{formatCurrency(quoteTotals.all)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-yellow-600">Pendentes:</span>
              <span className="font-medium text-yellow-600">{formatCurrency(quoteTotals.pending)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">Aprovados:</span>
              <span className="font-medium text-green-600">{formatCurrency(quoteTotals.approved)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-red-600">Rejeitados:</span>
              <span className="font-medium text-red-600">{formatCurrency(quoteTotals.rejected)}</span>
            </div>
          </div>

          <Tabs value={quotesFilter} onValueChange={(value) => setQuotesFilter(value as 'all' | 'pending' | 'approved' | 'rejected')}>
            <TabsList>
              <TabsTrigger value="all">Todos</TabsTrigger>
              <TabsTrigger value="pending">Pendentes</TabsTrigger>
              <TabsTrigger value="approved">Aprovados</TabsTrigger>
              <TabsTrigger value="rejected">Rejeitados</TabsTrigger>
            </TabsList>
            <TabsContent value={quotesFilter}>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {quotesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin" />
                  </div>
                ) : quotes.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Nenhum orcamento encontrado.
                  </p>
                ) : (
                  quotes.map((quote: Quote) => (
                    <QuoteMobileCard
                      key={quote.id}
                      quote={quote}
                      onOpenDetails={handleOpenQuoteDialog}
                    />
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              {quotesLoading ? (
                <div className="hidden md:flex items-center justify-center py-8">
                  <RefreshCw className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <QuotesTable
                  quotes={sortedQuotes}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  onOpenDetails={handleOpenQuoteDialog}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Quote Details Dialog */}
      <QuoteDetailsDialog
        quote={selectedQuote}
        open={quoteDetailsOpen}
        onOpenChange={setQuoteDetailsOpen}
        quoteEditMode={quoteEditMode}
        quoteStatusUpdating={quoteStatusUpdating}
        onEnterEditMode={handleEnterEditMode}
        onUpdateStatus={handleUpdateQuoteStatus}
        accessToken={accessToken}
        onQuoteUpdate={fetchQuotes}
      />

      {/* Edit Quote Dialog */}
      <EditQuoteDialog
        key={`${selectedQuote?.id}-${selectedQuote?.items?.length || 0}-${quoteEditMode}`}
        quote={selectedQuote}
        open={quoteEditMode}
        onClose={handleCancelEdit}
        onSave={handleSaveQuoteChanges}
        isUpdating={isUpdatingQuote}
        accessToken={accessToken}
      />
    </>
  );
}
